import {Component, Input, OnDestroy, OnInit, ViewContainerRef} from '@angular/core';
import {EntityIdDetails} from '../../../shared/models/entity-id-details.model';
import {Right, TransactionRule, TxRuleData, ReviewStatus} from '../right.model';
import {MdDialog, MdDialogConfig, MdDialogRef} from '@angular/material';
import {Agreement} from '../agreement.model';
import {SnackBarService} from '../../../services/snackbar.service';
import {EntityTransactionProfileService} from '../entity-transaction-profile.service';
import {MessageService} from '../../../services/messages.service';
import {AddRightComponent} from '../rights/addright/add-right.component';
import {AddTransactionRuleComponent} from './add-transaction-rule/add-transaction-rule.component';
import {MoveTransactionRuleDialogComponent} from './move-transaction-rule-dialog.component';
import {Observable} from 'rxjs/Observable';
import {Subscription} from 'rxjs/Subscription';
import {ConstantsService} from '../../../services/constants-service';
import {TransactionDescriptionBuilder} from './transaction-rule-descriptionn-builder';
import {BankAccount} from '../../../shared/models/bank-account.model';
import {ConfirmationDialogComponent} from '../../../shared/components/confirmationdialog/confirmation-dialog.component';
import {RuleHistoryDialogComponent} from '../../transaction-monitoring/rule-history-dialog/rule-history-dialog.component';
import {TestRuleTransactionsDialogComponent} from './test-rule-transactions-dialog.component';

@Component({
  selector: 'app-transaction-rule',
  templateUrl: './transaction-rule.component.html',
  styleUrls: ['./transaction-rule.component.css']
})
export class TransactionRuleComponent implements OnInit, OnDestroy {

  @Input() entityIdDetails: EntityIdDetails;
  rules: Array<TxRuleData>;
  dialogRef: MdDialogRef<any>;
  selectedTxRuleData: TxRuleData;
  isLoading = false;
  selectedAgreement: Agreement;
  zeroAggrement = true;
  dateFormat: string;
  bankAccounts: BankAccount[] = [];
  private subscriptions: Subscription[] = [];
  
  // Expose ReviewStatus enum to template
  ReviewStatus = ReviewStatus;

  constructor(
    public dialog: MdDialog,
    public viewContainerRef: ViewContainerRef,
    public  snackBarService:  SnackBarService,
    private transactionProfileService: EntityTransactionProfileService) { }

  ngOnInit() {
    this.dateFormat = ConstantsService.DATE_FORMAT;
    this.setSelectedAgreementSubscriber();
    this.loadBankAccounts();
  }

  loadBankAccounts() {
    if (this.entityIdDetails && this.entityIdDetails.entityId) {
      const subscription = this.transactionProfileService.getBankAccountsForEntity(this.entityIdDetails.entityId)
        .subscribe(
          accounts => {
            this.bankAccounts = accounts || [];
          },
          error => {
            console.error('Error loading bank accounts:', error);
            this.bankAccounts = [];
          }
        );
      this.subscriptions.push(subscription);
    }
  }

  getAccountName(accountNo: string): string {
    if (!accountNo || !this.bankAccounts || this.bankAccounts.length === 0) {
      return '-';
    }
    const account = this.bankAccounts.find(acc => acc.accountNumber === accountNo);
    if (account) {
      return account.accountName || account.accountNumber || '-';
    }
    return accountNo; // Return account number if name not found
  }

  getBankAccount(accountNo: string): string {
    if (!accountNo) {
      return '-';
    }
    return accountNo; // Return just the account number
  }

  getReviewStatusName(reviewStatus: ReviewStatus | number | string): string {
    if (reviewStatus === null || reviewStatus === undefined) {
      return 'N/A';
    }
    // Handle string values from backend (most common case)
    if (typeof reviewStatus === 'string') {
      return reviewStatus; // Backend sends "DRAFT", "FINAL", etc. as strings
    }
    // Handle enum values and numeric values
    if (reviewStatus === ReviewStatus.DRAFT || reviewStatus === 0) {
      return 'DRAFT';
    } else if (reviewStatus === ReviewStatus.FINAL || reviewStatus === 1) {
      return 'FINAL';
    } else if (reviewStatus === ReviewStatus.REVIEW || reviewStatus === 2) {
      return 'REVIEW';
    }
    return 'N/A';
  }

  setSelectedAgreementSubscriber() {
    // Unsubscribe from previous subscription if it exists
    const existingSub = this.subscriptions.find(sub => sub && !sub.closed);
    if (existingSub) {
      existingSub.unsubscribe();
    }
    
    this.isLoading = true;
    const subscription = this.transactionProfileService.selectedAgreement.subscribe(agreement => {
      if (agreement) {
        this.selectedAgreement = agreement;
        this.loadRights();
        this.zeroAggrement = false;
      }else {
        this.isLoading = false;
      }
    });
    this.subscriptions.push(subscription);
  }

  loadRights() {
    // Use the combined endpoint to get both Rights and Obligations in a single call
    this.isLoading = true;
    const subscription = this.transactionProfileService.getAllTransactionRules(this.selectedAgreement.id)
      .subscribe(response => {
        // Map Rights to TxRuleData
        const rightsData = response.rights.map((right) => {
          const txData: TxRuleData = {
            agreementId: right.agreementId,
            transactionRule: right.transactionRule,
            counterPartyId: right.counterPartyId,
            date: right.date,
            title: right.right,
            counterPartyName: right.counterPartyName,
            id: right.id,
            isRight: true // Mark as Right
          };
          return txData;
        });
        
        // Map Obligations to TxRuleData
        const obligationsData = response.obligations.map((obligation) => {
          const txData: TxRuleData = {
            agreementId: obligation.agreementId,
            transactionRule: obligation.transactionRule,
            counterPartyId: obligation.counterPartyId,
            date: obligation.date,
            title: obligation.obligation,
            counterPartyName: obligation.counterPartyName,
            id: obligation.id,
            isRight: false // Mark as Obligation
          };
          return txData;
        });
        
        // Combine both lists
        this.rules = [...rightsData, ...obligationsData];
        this.isLoading = false;
      }, error => {
        console.error('Error loading transaction rules:', error);
        this.rules = [];
        this.isLoading = false;
      });
    this.subscriptions.push(subscription);
  }

  loadObligations() {
    // This method is kept for backward compatibility but is no longer used
    // All transaction rules are now loaded via loadRights() which uses getAllTransactionRules()
  }

  editRigth(data: TxRuleData) {
    this.selectedTxRuleData = data;
    this.openAddDialog('Edit');
  }

  removeItem(rule: TxRuleData) {
    // First, get the rule history to count versions
    if (!rule.transactionRule || !rule.transactionRule.id) {
      this.callSnackBarService(null, '', 'Transaction rule ID is missing');
      return;
    }

    this.isLoading = true;
    const subscription = this.transactionProfileService.getTransactionRuleHistory(rule.transactionRule.id)
      .subscribe(
        history => {
          this.isLoading = false;
          const versionCount = history ? history.length : 0;
          
          // Show confirmation dialog with version count
          const confirmConfig = new MdDialogConfig();
          confirmConfig.width = '500px';
          confirmConfig.viewContainerRef = this.viewContainerRef;
          
          const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, confirmConfig);
          confirmDialogRef.componentInstance.title = 'Delete Transaction Rule';
          confirmDialogRef.componentInstance.message = 
            `Are you sure you want to delete this transaction rule? This will delete ${versionCount} version(s) of the rule. This action cannot be undone.`;
          
          const dialogSub = confirmDialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
              this.performDelete(rule);
            }
          });
          this.subscriptions.push(dialogSub);
        },
        error => {
          this.isLoading = false;
          console.error('Error loading rule history:', error);
          // If we can't get history, still show confirmation but without count
          const confirmConfig = new MdDialogConfig();
          confirmConfig.width = '500px';
          confirmConfig.viewContainerRef = this.viewContainerRef;
          
          const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, confirmConfig);
          confirmDialogRef.componentInstance.title = 'Delete Transaction Rule';
          confirmDialogRef.componentInstance.message = 
            'Are you sure you want to delete this transaction rule? This action cannot be undone.';
          
          const dialogSub = confirmDialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
              this.performDelete(rule);
            }
          });
          this.subscriptions.push(dialogSub);
        }
      );
    this.subscriptions.push(subscription);
  }

  private performDelete(rule: TxRuleData) {
    this.isLoading = true;
    
    // Use the isRight flag to determine if this is a Right or Obligation
    // If isRight is undefined, fall back to checking cashFlowName for backward compatibility
    let deleteObservable;
    if (rule.isRight === true) {
      // This is a Right
      deleteObservable = this.transactionProfileService.removeRight(rule.id);
    } else if (rule.isRight === false) {
      // This is an Obligation
      deleteObservable = this.transactionProfileService.removeObligation(rule.id);
    } else {
      // Fallback: try to determine from cashFlowName (for backward compatibility)
      const cashFlowName = rule.transactionRule ? rule.transactionRule.cashFlowName : null;
      if (cashFlowName === 'Incoming') {
        deleteObservable = this.transactionProfileService.removeRight(rule.id);
      } else {
        deleteObservable = this.transactionProfileService.removeObligation(rule.id);
      }
    }
    
    deleteObservable.subscribe(
      response => {
        this.loadRights();
        this.callSnackBarService(response, MessageService.RIGHT_DELETE_SUCCESS, '');
      },
      error => {
        this.callSnackBarService(error, '', MessageService.RIGHT_DELETE_FAILUR);
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  viewHistory(rule: TxRuleData) {
    if (!rule.transactionRule || !rule.transactionRule.id) {
      this.snackBarService.showStatus(null, '', 'Transaction rule ID is missing');
      return;
    }

    const config = new MdDialogConfig();
    config.width = '900px';
    config.height = '80%';
    config.viewContainerRef = this.viewContainerRef;
    config.data = { 
      rule: rule.transactionRule,
      clientId: this.entityIdDetails ? this.entityIdDetails.clientId : null
    };
    
    const historyDialogRef = this.dialog.open(RuleHistoryDialogComponent, config);
    
    const subscription = historyDialogRef.afterClosed().subscribe(result => {
      // If a version was restored, reload the rules
      if (result && result.restored) {
        this.loadRights();
      }
    });
    this.subscriptions.push(subscription);
  }

  makeFinal(rule: TxRuleData) {
    if (!rule.transactionRule || !rule.transactionRule.id) {
      this.snackBarService.showStatus(null, '', 'Transaction rule ID is missing');
      return;
    }

    // Check if rule is DRAFT (handle both string and enum values)
    const reviewStatus: any = rule.transactionRule.reviewStatus;
    const isDraft = reviewStatus === 'DRAFT' || 
                    reviewStatus === ReviewStatus.DRAFT || 
                    reviewStatus === 0;
    if (!isDraft) {
      this.snackBarService.showStatus(null, '', 'Only DRAFT rules can be made FINAL');
      return;
    }

    if (!this.entityIdDetails || !this.entityIdDetails.clientId) {
      this.snackBarService.showStatus(null, '', 'Client ID is missing');
      return;
    }

    this.isLoading = true;
    
    // Clone the transaction rule and set status to FINAL
    const updatedRule: TransactionRule = Object.assign({}, rule.transactionRule);
    updatedRule.reviewStatus = ReviewStatus.FINAL;

    const subscription = this.transactionProfileService.updateTransactionRuleStatus(
      this.entityIdDetails.clientId,
      updatedRule
    ).subscribe(
      response => {
        this.isLoading = false;
        this.loadRights(); // Reload the rules to show updated status
        this.snackBarService.showStatus(response, 'Transaction rule status updated to FINAL', '');
      },
      error => {
        this.isLoading = false;
        console.error('Error updating transaction rule status:', error);
        this.snackBarService.showStatus(error, '', 'Failed to update transaction rule status');
      }
    );
    this.subscriptions.push(subscription);
  }

  openAddDialog(action: string) {
    const config = new MdDialogConfig();
    // config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(AddTransactionRuleComponent, config);
    this.dialogRef.componentInstance.selectedTxRule = this.selectedTxRuleData;
    this.dialogRef.componentInstance.entityIdDetails = this.entityIdDetails;
    this.dialogRef.componentInstance.agreementId = this.selectedAgreement.id;
    this.dialogRef.componentInstance.action = action;
    const subscription = this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadRights();
      }
      this.dialogRef = null;
      this.selectedTxRuleData = null;
    });
    this.subscriptions.push(subscription);
  }

  callSnackBarService(response: any, success: string, failur: string) {
    this.isLoading = false;
    this.snackBarService.showStatus(response, success, failur);
  }

  moveTransactionRule(rule: TxRuleData) {
    const config = new MdDialogConfig();
    config.disableClose = false;
    config.width = '90vw';
    config.viewContainerRef = this.viewContainerRef;
    config.data = {
      txRuleData: rule,
      entityIdDetails: this.entityIdDetails
    };

    this.dialogRef = this.dialog.open(MoveTransactionRuleDialogComponent, config);
    
    const subscription = this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload transaction rules after moving
        this.loadRights();
      }
      this.dialogRef = null;
    });
    this.subscriptions.push(subscription);
  }

  openTestRuleTransactionsDialog(rule: TxRuleData) {
    const config = new MdDialogConfig();
    config.disableClose = false;
    config.width = '90vw';
    config.height = '80%';
    config.viewContainerRef = this.viewContainerRef;
    config.data = {
      rule: rule,
      entityIdDetails: this.entityIdDetails
    };

    this.dialogRef = this.dialog.open(TestRuleTransactionsDialogComponent, config);

    const subscription = this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
      // No additional action required after closing.
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions to prevent memory leaks and duplicate calls
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

}



// WEBPACK FOOTER //
// ./src/app/entities/transaction-profile/transaction-rule/transaction-rule.component.ts